import React, { useState, useEffect, useCallback } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Button, Space, Typography, AutoComplete, Input, Row, Col, Select, Tooltip } from "antd";
import { withRouter } from "react-router-dom";
import { useLocation } from "react-router";
import { withTranslation } from "react-i18next";
import { withOktaAuth } from "@okta/okta-react";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { debounce } from "lodash";
import styled from "styled-components";
import InputMask from "react-input-mask";
import { useCookies } from "react-cookie";
import { SwitchingBetweenApps } from "@tailormed/common-client/component/SwitchingBetweenApps";
import { Link } from "react-router-dom";

import "./MainHeader.css";
import { Routes } from "../constant/routes";
import { selectFirstRecentPatients } from "@tailormed/common-client/store/reselectSelector";
import {
  selectSearchedPatients,
  selectIsFetching,
  selectUser,
  selectViewType,
  selectFilterList,
  selectSavedListViewType
} from "../store/selector";
import ACTIONS from "../store/action";
import { ApiNames } from "../api/api";
import NoResultIcon from "../assets/svg/NoResultIcon";
import { HighlightResult, SearchTermWithTooltip } from "./ShorterTextWithTooltip";
import { HeaderMenu } from "./HeaderMenu";
import {
  formatPhoneNumberToDisplay,
  decodeDate,
  decodeNumbersForSearch,
  formatSSNToDisplay,
  formatPhoneNumber,
  stripHtml
} from "../utils/formaters";
import { isModifierClick } from "../utils/misc";
import { selectSearchedText } from "../store/selector";
import { AppLayout, TailormedLogoContainer } from "../component/AppLayout";
import {
  GRAY_100,
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_500,
  GRAY_50,
  GRAY_900,
  PRIMARY_600,
  WHITE
} from "../constant/colors";
import { fontWeights, sizes } from "../constant/styles";
import { useGetSetting } from "../hooks/getSetting";
import { customerTypes } from "../constant/customerType";
import { displayedMRN } from "@tailormed/common-client/util/mrn";
import { searchTypes, ViewType } from "../constant/general";
import { STORAGE_KEYS } from "../constant/storageKeys";
import { ReactComponent as TailormedVisualIcon } from "../assets/svg/tailormed-logo-icon.svg";
import { ReactComponent as VerticalLineIcon } from "../assets/svg/vertical-line-icon.svg";
import { ReactComponent as PharmacyIcon } from "../assets/svg/pharmacy-icon.svg";
import { ReactComponent as ArrowDownIcon } from "../assets/svg/arrow-down-icon.svg";
import { HistoryOutlined, LoadingOutlined } from "@ant-design/icons";

const { Text } = Typography;
const InputGroup = Input.Group;
const { Option } = Select;
const HEADER_WIDTH = 700;
const DROP_DOWN_LIST_WIDTH = 112;
const HOME_NUMBER = "home_number";
const NAME_ID = searchTypes.name_id.key;
const DOB = searchTypes.dob.key;
const PHONE = searchTypes.phone_number.key;
const SSN = searchTypes.social_security_number.key;
const ONE_YEAR = 31536000; // in seconds: 60*60*24*365
const PHONE_LENGTH_WITHOUT_FORMAT = 10;

const StyledInputGroup = styled(InputGroup)`
  border: 1px solid ${GRAY_300};
  border-radius: 5px;
  &:hover {
    border: 1px solid ${GRAY_400};
  }

  &:focus {
    border: 1px solid ${PRIMARY_600};
  }

  &:focus-within {
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    border: 1px solid ${PRIMARY_600};
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    height: 36px;
    background: ${GRAY_50};
    align-items: center;
    color: ${GRAY_900};
    border: 0px;
    border-right: 1px solid ${GRAY_300};
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    font-size: ${sizes.medium};
    &:hover {
      background: ${GRAY_100};
    }
    &:focus-within {
      border-color: transparent;
      box-shadow: none;
      border-right: 1px solid #d0d5dd;
    }
  }

  .ant-select-single.ant-select-open .ant-select-selection-item {
    color: ${GRAY_900};
    background: ${GRAY_100};
    box-shadow: 0px;
    border: 0px;
    outline-color: transparent;
    outline-offset: unset;
    outline-width: 0px;
  }

  .ant-select-arrow {
    color: ${GRAY_900};
  }

  .ant-select-single .ant-select-selector .ant-select-selection-search-input {
    height: 36px;
    padding-left: 12px;
    background: ${WHITE};
    border: 0px;
    border-left: 1px solid ${GRAY_300};
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    border-radius: 0px 4px 4px 0px;
    font-size: ${sizes.medium};
    color: ${GRAY_900};
    &:focus {
      outline-color: transparent;
      outline-offset: unset;
      outline-width: 0px;
    }
    &::placeholder {
      color: ${GRAY_400};
    }
  }
`;
const SearchResultsTitle = styled(Text)`
  font-family: Open Sans;
  font-style: normal;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  line-height: 14px;
  letter-spacing: 0.15px;
  color: ${GRAY_500};
`;

const StyledPatientInfo = styled.div`
  font-size: ${sizes.medium};
  ${(props) => props.recentPatients && `font-weight: ${fontWeights.semibold};`}
  color: ${GRAY_900};
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding-right: 10px;
  padding-bottom: 7px;
`;

const StyledPatientMeta = styled.span`
  font-size: ${sizes.small};
  font-weight: ${fontWeights.regular};
  color: ${GRAY_500};
`;

const StyledLoaderContainer = styled.div`
  color: ${PRIMARY_600};
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const textOptionsListStyle = {
  fontSize: sizes.medium,
  fontWeight: fontWeights.regular,
  color: GRAY_900
};

const LogoButton = styled(Button)`
  margin-left: 8px;
  padding: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;

  span {
    color: ${WHITE};
    font-size: ${sizes.medium};
    font-weight: ${fontWeights.semibold};
  }

  > svg:nth-last-child(1) {
    margin-left: 4px;
    margin-top: 2px;
    path {
      fill: ${WHITE};
    }
  }

  :hover {
    background: rgba(245, 250, 255, 0.2);
    border-radius: 8px;
  }

  :active {
    background: rgba(239, 248, 255, 0.2);
    border-radius: 8px;
  }
`;

const MainHeaderComponent = ({
  t,
  history,
  isSearchFetching,
  isRecentPatientsFetching,
  searchedPatients,
  firstRecentPatients,
  authState
}) => {
  let [idPrefix, customerType] = useGetSetting(["id_prefix", "fe-customer-type"]);
  customerType = customerType.toLowerCase();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const viewType = useSelector(selectViewType);
  const filterList = useSelector(selectFilterList);
  const savedListViewType = useSelector(selectSavedListViewType);

  const [cookies, setCookie] = useCookies(["searchBy"]);
  const searchByDefaultValue = cookies["searchBy"] ?? (customerType === customerTypes.RETAIL ? PHONE : NAME_ID);
  const searchText = useSelector(selectSearchedText);
  const [searchBy, setSearchBy] = useState(searchByDefaultValue);
  const [isSearchFetchingPatients, setisSearchFetchingPatients] = useState(isSearchFetching);
  const [isRecentPatientsFetchingPatients, setisRecentPatientsFetchingPatients] = useState(isRecentPatientsFetching);
  const [open, setOpen] = useState(false);
  const [startRecentPatients, setStartRecentPatients] = useState(false);
  const location = useLocation();
  const { search, state } = useLocation();
  const searchParams = new URLSearchParams(search);
  const searchByParams = searchParams.get("searchBy") || NAME_ID;
  const searchTextByParams = state?.searchText;
  const { patients = [] } = searchText ? searchedPatients || {} : firstRecentPatients || {};
  const notPatientPage = !(location.pathname.split("/")[1] !== "patient");
  const enablePhoneMasking = searchText?.length < 4 && searchBy === PHONE;
  const searchResultsPage = location.pathname.includes(Routes.SEARCH_RESULTS);
  const showRequiredLengthMessage = searchBy !== NAME_ID && searchText?.length < searchTypes[searchBy]?.maxLength;
  const setSearchTextAct = (searchText) => dispatch(ACTIONS.setSearchText(searchText));
  const selectSearchedPatient = (pharmacyPatient, patientId, orderId) =>
    dispatch(ACTIONS.setPatientAction(pharmacyPatient, patientId, orderId));
  const searchPatientsAct = (page, text, searchBy) => dispatch(ACTIONS.searchPatientsAction(page, text, searchBy));
  const recentPatientsAct = (page) => dispatch(ACTIONS.getRecentPatientsAction(page, true));
  const setPatientModal = (show, tab) => dispatch(ACTIONS.setShowPatientModal(show, tab));
  const restSearchResult = () => dispatch(ACTIONS.clearSearchedPatients());
  const getPatientOrderAct = (orderId) => dispatch(ACTIONS.getPatientOrderAction(orderId));
  const setSearchedPatientAct = (patient) => dispatch(ACTIONS.setSearchedPatient(patient));
  const setOrdersTableSearchTextAct = (ordersTableSearchText) =>
    dispatch(ACTIONS.setOrdersTableSearchText(ordersTableSearchText));

  useEffect(() => {
    if (!searchResultsPage && (patients.length || searchText)) {
      resetAutoComplete();
    }
    if (searchResultsPage) {
      setSearchBy(searchByParams);
      setSearchTextAct(searchTextByParams || localStorage.getItem(STORAGE_KEYS.SEARCH_RESULTS_CURRENT_TEXT));
    }

    setCookie("searchBy", searchByParams, { path: "/", maxAge: ONE_YEAR });
  }, [location]);

  useEffect(() => {
    if ([DOB, PHONE, SSN].includes(searchBy)) {
      searchText?.length != searchTypes[searchBy]?.maxLength && restSearchResult();
    }
    //NOTE : this line to remove "(" automatic when remove last number in phone number
    if (searchText === "(" && searchBy === PHONE) setSearchTextAct("");
  }, [searchText, searchBy]);

  useEffect(() => {
    setisSearchFetchingPatients(isSearchFetching);
  }, [isSearchFetching]);

  useEffect(() => {
    setisRecentPatientsFetchingPatients(isRecentPatientsFetchingPatients);
  }, [isRecentPatientsFetchingPatients]);

  useEffect(() => {
    setSearchBy(searchByDefaultValue);
  }, [customerType]);

  useEffect(() => {
    setStartRecentPatients(isRecentPatientsFetching);
  }, [isRecentPatientsFetching]);

  const handleSearch = (text) => {
    let formatedQuery = text;
    if (typeof formatedQuery !== "string") {
      formatedQuery = searchText;
    }
    let shouldFetchQuery = false;

    switch (searchBy) {
      case SSN:
      case PHONE: {
        formatedQuery?.length === searchTypes[searchBy]?.maxLength &&
          (searchPatientsAct(1, decodeNumbersForSearch(formatedQuery), searchBy), (shouldFetchQuery = true));
        break;
      }
      case DOB: {
        formatedQuery?.length === searchTypes[searchBy]?.maxLength &&
          (searchPatientsAct(1, formatedQuery, searchBy), (shouldFetchQuery = true));
        break;
      }
      case NAME_ID: {
        if (formatedQuery) {
          setisSearchFetchingPatients(true);
        } else {
          setisSearchFetchingPatients(true);
          setStartRecentPatients(true);
        }
        setisSearchFetchingPatients(true);
        debouncedSearch(formatedQuery, searchBy);
        shouldFetchQuery = true;
        break;
      }
    }

    if (!open) {
      setOpen(true);
    }
    setSearchTextAct(formatedQuery);

    if (searchResultsPage && shouldFetchQuery) {
      history.replace(`${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText: formatedQuery });
    }
  };

  const handleChangeSelect = (value) => {
    setSearchBy(value);
    resetAutoComplete();
    setCookie("searchBy", value, { path: "/", maxAge: ONE_YEAR });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (!searchText) {
        return history.replace(`${Routes.ROOT}${Routes.RECENT_PATIENTS}`);
      } else {
        setOrdersTableSearchTextAct("");
        if (patients.length > 0) {
          setSearchTextAct(searchText);
          history.replace(`${Routes.ROOT}${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText });
        }
      }
    }
  };

  const debouncedSearch = useCallback(
    debounce(
      (nextValue, searchBy) => (nextValue ? searchPatientsAct(1, nextValue, searchBy) : recentPatientsAct(1)),
      500
    ),
    []
  );

  const resetAutoComplete = () => {
    setSearchTextAct("");
    restSearchResult();
  };

  const handlePaste = (e) => {
    let formatedQuery = e.clipboardData.getData("text");

    if (formatedQuery.length == PHONE_LENGTH_WITHOUT_FORMAT && !formatedQuery.includes("(") && searchBy == PHONE) {
      searchPatientsAct(1, decodeNumbersForSearch(formatedQuery), searchBy);
      setSearchTextAct(formatPhoneNumberToDisplay(formatedQuery));
    }
  };
  const goToHomePage = () => {
    history.replace(Routes.ROOT + "list");
  };

  const handleLogoButtonClick = (hasModulesToSwitch) => {
    if (!hasModulesToSwitch) {
      goToHomePage();
    }
  };

  const displayLoader = !searchText && (startRecentPatients || isRecentPatientsFetching);

  const menuPendoId = searchText ? "patient_search_menu" : "recent_patients_menu";
  const showMorePendoId = searchText ? "header_search_show_more" : "header_recent_patients_show_more";

  return (
    <AppLayout>
      <Row align="middle">
        <Col span={6}>
          <TailormedLogoContainer>
            <Button type="text" onClick={goToHomePage}>
              <TailormedVisualIcon />
            </Button>
            <VerticalLineIcon />
            <SwitchingBetweenApps
              user={user}
              goToHomePage={goToHomePage}
              useGetSetting={useGetSetting}
              pathname={location.pathname}
            >
              {({ hasModulesToSwitch }) => (
                <LogoButton
                  type="text"
                  icon={<PharmacyIcon />}
                  id="pharmacy"
                  onClick={() => handleLogoButtonClick(hasModulesToSwitch)}
                >
                  {t("pharmacy")}
                  {hasModulesToSwitch && <ArrowDownIcon />}
                </LogoButton>
              )}
            </SwitchingBetweenApps>
          </TailormedLogoContainer>
        </Col>
        <Col span={12}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            {authState?.isAuthenticated && (
              <StyledInputGroup
                compact
                style={{
                  width: HEADER_WIDTH,
                  display: "flex",
                  marginRight: notPatientPage ? 42 : 0
                }}
              >
                <Select
                  id="search_criteria"
                  defaultValue={searchByDefaultValue}
                  value={searchBy}
                  onChange={handleChangeSelect}
                  placement="bottomRight"
                  dropdownMatchSelectWidth={DROP_DOWN_LIST_WIDTH}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  <Option value={NAME_ID} id="searchbar_name">
                    {t("header_searchbar.option_type_list.name_id")}
                  </Option>
                  <Option value={DOB} id="searchbar_dob">
                    {t("header_searchbar.option_type_list.dob")}
                  </Option>
                  <Option value={PHONE} id="searchbar_phone">
                    {t("header_searchbar.option_type_list.phone")}
                  </Option>
                  <Option value={SSN} id="searchbar_ssn">
                    {t("header_searchbar.option_type_list.ssn")}
                  </Option>
                </Select>

                {open && !searchText && (
                  <div
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "5px",
                      zIndex: 1000,
                      fontSize: sizes.large,
                      fontWeight: fontWeights.bold
                    }}
                  >
                    <HistoryOutlined />
                  </div>
                )}

                <AutoComplete
                  id="general_search_input"
                  data-tm-id="search"
                  autoFocus={searchText && true}
                  loading={isSearchFetching}
                  notFoundContent={
                    searchText &&
                    !isSearchFetchingPatients && (
                      <>
                        {showRequiredLengthMessage && (
                          <Text>{t(`header_searchbar.autocomplete_search_results_list.${searchBy}`)}</Text>
                        )}
                        <Space direction="vertical" style={{ width: "100%", height: "100%", alignItems: "center" }}>
                          <div
                            style={{ display: "flex", justifyContent: "center", alignItems: "center", color: GRAY_400 }}
                          >
                            <NoResultIcon />
                          </div>
                          {!showRequiredLengthMessage && (
                            <Text
                              id="header_search_no_results"
                              style={{
                                color: GRAY_400,
                                fontWeight: fontWeights.semibold,
                                textAlign: "center",
                                fontSize: sizes.medium_large
                              }}
                            >
                              {t(`header_searchbar.autocomplete_search_results_list.no_results`)}
                            </Text>
                          )}
                        </Space>
                      </>
                    )
                  }
                  style={{ flexBasis: "100%" }}
                  dropdownStyle={{
                    borderRadius: "8px",
                    boxShadow: "0px 12px 16px -4px rgba(16, 24, 40, 0.1), 0px 4px 6px -2px rgba(16, 24, 40, 0.05)",
                    background: WHITE,
                    padding: "12px 0px"
                  }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  allowClear={!isSearchFetching}
                  onSearch={handleSearch}
                  onFocus={handleSearch}
                  onBlur={() => {
                    // This allows us to click show more and it works
                    setTimeout(() => {
                      setOpen(false);
                    }, 200);
                  }}
                  open={open}
                  onPaste={handlePaste}
                  onSelect={(value, option) => {
                    document.getElementById("general_search_input").blur();
                    setOpen(false);
                    setPatientModal(true, 0);
                    selectSearchedPatient(option.pharmacy_patient, option.patient_id, option.currentOrder_id);
                    const selectedPatient = patients.find((p) => p.id === option.pharmacy_patient);
                    setSearchedPatientAct(selectedPatient);
                    getPatientOrderAct(option.pharmacy_patient);
                  }}
                  onDropdownVisibleChange={(isOpen) => {
                    if (isOpen) {
                      // Scroll the dropdown to the top each time it opens
                      setTimeout(function () {
                        const lists = document.getElementsByClassName("rc-virtual-list-holder");
                        for (let i = 0; i <= lists.length; i++) {
                          if (lists.item(i)) {
                            lists.item(i).scrollTop = 0;
                          }
                        }
                      }, 50);
                    }
                  }}
                  value={searchText}
                  onClear={resetAutoComplete}
                  onKeyDown={handleKeyDown}
                  dropdownMatchSelectWidth={!patients.length && null}
                  dropdownRender={(menu) => (
                    <>
                      <div id={menuPendoId} className={displayLoader ? "patient_search_menu_loading" : ""}>
                        {menu}
                      </div>
                      {patients.length > 3 && (
                        <Row justify="center" style={{ borderTop: `1px solid ${GRAY_200}` }}>
                          <Col>
                            <Button
                              id={showMorePendoId}
                              type="link"
                              onClick={() => {
                                searchText
                                  ? history.replace(`${Routes.ROOT}${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, {
                                      searchText
                                    })
                                  : history.replace(`${Routes.ROOT}${Routes.RECENT_PATIENTS}`);
                                setOpen(false);
                              }}
                              style={{ paddingLeft: 10 }}
                            >
                              <Text
                                style={{
                                  color: PRIMARY_600,
                                  cursor: "pointer",
                                  fontSize: sizes.medium,
                                  fontWeight: fontWeights.semibold,
                                  lineHeight: "20px",
                                  fontStyle: "normal",
                                  marginTop: "5px"
                                }}
                              >
                                {t("header_searchbar.show_more")}
                              </Text>
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                  options={
                    !searchText && !startRecentPatients && !patients.length
                      ? [
                          {
                            options: [
                              {
                                value: "notfound",
                                label: (
                                  <Space
                                    direction="vertical"
                                    style={{ width: "100%", height: "100%", alignItems: "center" }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        color: GRAY_400
                                      }}
                                    >
                                      <NoResultIcon />
                                    </div>
                                    <Text
                                      id="header_search_no_results"
                                      style={{
                                        color: GRAY_400,
                                        fontWeight: fontWeights.semibold,
                                        textAlign: "center",
                                        fontSize: sizes.medium_large
                                      }}
                                    >
                                      {t(`header_searchbar.autocomplete_search_results_list.no_results`)}
                                    </Text>
                                  </Space>
                                )
                              }
                            ]
                          }
                        ]
                      : displayLoader
                      ? [
                          {
                            label: undefined,
                            options: [
                              {
                                value: "loading",
                                label: (
                                  <StyledLoaderContainer>
                                    <LoadingOutlined style={{ fontSize: "35px" }} />
                                  </StyledLoaderContainer>
                                )
                              }
                            ]
                          }
                        ]
                      : patients.length === 0
                      ? []
                      : [
                          {
                            label: (
                              <>
                                <Row>
                                  <Col span={11}>
                                    <SearchResultsTitle>
                                      {searchText
                                        ? t("header_searchbar.searched_patients_name_id")
                                        : t("header_searchbar.recent_patients_name")}
                                    </SearchResultsTitle>
                                  </Col>
                                  <Col span={5}>
                                    <SearchResultsTitle>
                                      {t(
                                        `header_searchbar.search_results_title.${
                                          searchBy === "name_id" || !searchText ? "dob" : searchBy
                                        }`
                                      )}
                                    </SearchResultsTitle>
                                  </Col>
                                  <Col span={5}>
                                    <SearchResultsTitle>
                                      {t("header_searchbar.searched_patients_drug")}
                                    </SearchResultsTitle>
                                  </Col>
                                  <Col span={3}>
                                    <SearchResultsTitle>{t("header_searchbar.active_column")}</SearchResultsTitle>
                                  </Col>
                                </Row>
                              </>
                            ),
                            options: patients.slice(0, 10).map((patient) => {
                              const {
                                id: pharmacyPatient,
                                patient: {
                                  id,
                                  first_name: firstName,
                                  middle_name: middleName,
                                  last_name: lastName,
                                  mrn
                                },
                                rxnormDrug,
                                isActive,
                                currentOrder
                              } = patient;

                              const displayedPatientId = stripHtml(`${idPrefix ? `${idPrefix}-` : ""}${id}`);
                              const displayedMrn = stripHtml(
                                customerType === customerTypes.RETAIL
                                  ? searchBy === PHONE
                                    ? ""
                                    : t("columns.record_phone", { mrnRecord: formatPhoneNumber(mrn) })
                                  : t("header_searchbar.record_mrn", { mrnRecord: displayedMRN(mrn) })
                              );
                              const displayedPatientName = stripHtml(
                                firstName ? `${firstName}${middleName ? ` ${middleName}` : ""} ${lastName}` : "Patient"
                              );

                              const patientQueryParams = `?patient=${id}&pharmacyPatient=${pharmacyPatient}`;
                              const linkPathWithSavedList = `${Routes.ROOT}${Routes.SAVEDLISTS}/${filterList.id}/${
                                savedListViewType || ViewType.List.key
                              }${patientQueryParams}`;

                              const linkPath = filterList?.id
                                ? linkPathWithSavedList
                                : `${Routes.ROOT}${viewType || ViewType.List.key}${patientQueryParams}`;

                              return {
                                value: `${
                                  firstName
                                    ? `${firstName}${middleName ? ` ${middleName}` : ""} ${lastName}`
                                    : "Patient"
                                } - ${pharmacyPatient}`,
                                label: (
                                  <Link
                                    to={linkPath}
                                    onClick={(e) => {
                                      if (!isModifierClick(e)) {
                                        e.preventDefault();
                                      }
                                    }}
                                  >
                                    <Row>
                                      <Col span={11} style={{ display: "flex", flexDirection: "column" }}>
                                        <Tooltip
                                          title={
                                            <Text>
                                              <span id="tooltip_header_search_patient_name_mrn_id">
                                                <StyledPatientInfo recentPatients={!searchText}>
                                                  {displayedPatientName}
                                                  <br />
                                                  <StyledPatientMeta
                                                    id={`tooltip_header_search_patient_${
                                                      customerType === customerTypes.RETAIL ? "phone" : "mrn"
                                                    }_id`}
                                                  >
                                                    {displayedMrn} ({displayedPatientId})
                                                  </StyledPatientMeta>
                                                </StyledPatientInfo>
                                              </span>
                                            </Text>
                                          }
                                          color={"white"}
                                          placement={"left"}
                                        >
                                          <StyledPatientInfo
                                            recentPatients={!searchText}
                                            id="header_search_patient_name"
                                          >
                                            {!searchText && (
                                              <StyledPatientMeta style={{ fontSize: sizes.medium }}>
                                                <HistoryOutlined />{" "}
                                              </StyledPatientMeta>
                                            )}
                                            <span
                                              dangerouslySetInnerHTML={{
                                                __html: HighlightResult(`${displayedPatientName}`, searchText)
                                              }}
                                            />{" "}
                                            {patient?.patient[HOME_NUMBER] &&
                                              patient?.patient[PHONE] &&
                                              searchBy === PHONE &&
                                              searchText && <br />}
                                            <StyledPatientMeta
                                              id={`header_search_patient_${
                                                customerType === customerTypes.RETAIL ? "phone" : "mrn"
                                              }_id`}
                                            >
                                              <span
                                                dangerouslySetInnerHTML={{
                                                  __html: HighlightResult(
                                                    `${displayedMrn} (${displayedPatientId})`,
                                                    searchText
                                                  )
                                                }}
                                              />
                                            </StyledPatientMeta>
                                          </StyledPatientInfo>
                                        </Tooltip>
                                      </Col>
                                      <Col span={5}>
                                        {((searchBy === NAME_ID && patient?.patient[DOB]) ||
                                          !searchText ||
                                          patient?.patient[searchBy]) && (
                                          <SearchTermWithTooltip
                                            text={
                                              searchBy === DOB || searchBy === NAME_ID || !searchText
                                                ? decodeDate(patient.patient[DOB])
                                                : searchBy === PHONE
                                                ? formatPhoneNumberToDisplay(patient.patient[searchBy])
                                                : searchBy === SSN
                                                ? formatSSNToDisplay(patient.patient[searchBy])
                                                : patient.patient[searchBy]
                                            }
                                            id={`header_search_patient${
                                              searchBy === DOB || searchBy === NAME_ID || !searchText
                                                ? "_dob"
                                                : searchBy === PHONE
                                                ? "_phone_number"
                                                : searchBy === SSN
                                                ? "_ssn"
                                                : ""
                                            }`}
                                            searchInput={searchText}
                                            maxLength={15}
                                            textStyle={textOptionsListStyle}
                                          />
                                        )}
                                        {patient?.patient[HOME_NUMBER] && searchBy === PHONE && (
                                          <SearchTermWithTooltip
                                            text={formatPhoneNumberToDisplay(patient?.patient[HOME_NUMBER])}
                                            searchInput={searchText}
                                            maxLength={15}
                                            textStyle={textOptionsListStyle}
                                            id="header_search_patient_home_number"
                                          />
                                        )}
                                      </Col>
                                      <Col span={5}>
                                        <SearchTermWithTooltip
                                          text={rxnormDrug?.tmDisplayName || ""}
                                          maxLength={13}
                                          textStyle={textOptionsListStyle}
                                          id="header_search_patient_drug"
                                        />
                                      </Col>
                                      <Col span={3}>
                                        <SearchTermWithTooltip
                                          text={
                                            isActive ? t("header_searchbar.active") : t("header_searchbar.in_active")
                                          }
                                          maxLength={20}
                                          textStyle={textOptionsListStyle}
                                          id="header_search_patient_active"
                                        />
                                      </Col>
                                    </Row>
                                  </Link>
                                ),
                                patient_id: id,
                                pharmacy_patient: pharmacyPatient,
                                currentOrder_id: currentOrder.id
                              };
                            })
                          }
                        ]
                  }
                >
                  <InputMask
                    mask={!enablePhoneMasking && searchTypes[searchBy]?.maskInput}
                    maskPlaceholder=""
                    placeholder={t(`header_searchbar.placeholder.${searchBy}`)}
                    alwaysShowMask={false}
                  />
                </AutoComplete>
              </StyledInputGroup>
            )}
          </div>
        </Col>
        <Col span={6}>
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", cursor: "pointer" }}>
            <HeaderMenu />
          </div>
        </Col>
      </Row>
    </AppLayout>
  );
};

const mapStateToProps = createStructuredSelector({
  isSearchFetching: selectIsFetching([ApiNames.SearchFreeText]),
  isRecentPatientsFetching: selectIsFetching([ApiNames.GetRecentPatients]),
  searchedPatients: selectSearchedPatients,
  firstRecentPatients: selectFirstRecentPatients
});

const MainHeader = compose(withOktaAuth, withRouter, withTranslation(), connect(mapStateToProps))(MainHeaderComponent);
export { MainHeader };
